import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from './BrandLogo';
import {
  Home, FolderOpen, FileText, Layers, Video, Star, Music2, Wand2,
  Download, Plus, Beaker, Bot, LogOut, Shield, AlertTriangle
} from 'lucide-react';

// Canonical nav — identical items/order/styling to Dashboard & ProjectView.
const SIDEBAR_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, filter: 'all' },
  { id: 'projects', label: 'Projects', icon: FolderOpen, filter: 'all' },
  { id: 'script', label: 'Script', icon: FileText, filter: 'stage-script' },
  { id: 'preproduction', label: 'Pre-Production', icon: Layers, filter: 'stage-preproduction' },
  { id: 'production', label: 'Production', icon: Video, filter: 'stage-production' },
  { id: 'assets', label: 'Assets', icon: Star, filter: 'favorites' },
  { id: 'audio', label: 'Audio', icon: Music2, filter: 'all' },
  { id: 'postprod', label: 'Post-Production', icon: Wand2, filter: 'stage-postproduction' },
  { id: 'exports', label: 'Exports', icon: Download, filter: 'all' },
];

// Shared left panel used by pages outside the Dashboard (e.g. Recipes) so the
// sidebar stays visually identical across the whole app. Nav clicks route back
// to the Dashboard with the matching stage filter via location state.
export const GlobalSidebar = ({ active, recipeCount, sidebarOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const goDashboard = (item) => {
    onClose?.();
    navigate('/', { state: { activeNav: item.label, sidebarFilter: item.filter } });
  };

  return (
    <>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`
        fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50
        w-56 flex flex-col overflow-hidden
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: 'linear-gradient(180deg, #0B1120 0%, #101B2E 50%, #0D1526 100%)', borderRight: '1px solid rgba(56, 189, 248, 0.08)' }}
        data-testid="global-sidebar">
        <BrandLogo />

        <div className="h-12 px-3 flex items-center border-b" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <button onClick={() => { onClose?.(); navigate('/', { state: { openCreate: true } }); }}
            className="w-full px-4 py-2 rounded-lg font-ai font-semibold text-xs uppercase tracking-wider transition-all hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #162D4A 100%)', color: '#94C4F7', boxShadow: '0 4px 15px rgba(30, 58, 95, 0.4)', border: '1px solid rgba(56, 189, 248, 0.12)' }}
            data-testid="new-project-btn">
            <Plus className="w-4 h-4 inline mr-2" />New Project
          </button>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-hidden">
          {SIDEBAR_NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => goDashboard(item)}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-ai font-semibold uppercase tracking-wider transition-all duration-200 hover:bg-[#162D4A]/50"
              style={{ color: '#64748B' }}
              data-testid={`sidebar-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          ))}

          <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(245, 158, 11, 0.12)' }}>
            <p className="px-3 mb-2 text-[9px] font-ai font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(245, 158, 11, 0.5)' }}>Creative Tools</p>
            <button onClick={() => { onClose?.(); navigate('/recipes'); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B', ...(active === 'recipes' ? { background: 'rgba(245, 158, 11, 0.10)' } : {}) }}
              data-testid="sidebar-recipes">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Beaker className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>Recipes</span>
              <span className="ml-auto text-[9px] font-bold" style={{ color: 'rgba(245, 158, 11, 0.4)' }} data-testid="sidebar-recipe-count">{recipeCount ?? ''}</span>
            </button>
            <button onClick={() => { onClose?.(); navigate('/llm-studio'); }}
              className="w-full mt-1 flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B', ...(active === 'llms' ? { background: 'rgba(245, 158, 11, 0.10)' } : {}) }}
              data-testid="sidebar-llms">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Bot className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>LLMs</span>
              <span className="ml-auto text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.20)', color: '#c7d2fe' }}>NEW</span>
            </button>
          </div>
        </nav>

        <div className="p-3 border-t relative z-[70]" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#162D4A]/50 transition-colors">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(56, 189, 248, 0.1)' }}>
              <span className="text-xs font-ai" style={{ color: '#7DD3FC' }}>{user?.name?.[0] || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-ai truncate" style={{ color: '#CBD5E1' }}>{user?.name || 'User'}</p>
            </div>
            <button onClick={logout} className="p-1.5 hover:bg-[#162D4A]/50 rounded transition-colors" title="Sign out" data-testid="sidebar-logout">
              <LogOut className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
            </button>
          </div>
          {user?.is_admin && (
            <button onClick={() => navigate('/admin')} className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50" style={{ color: '#7DD3FC' }} data-testid="sidebar-admin-link">
              <Shield className="w-3.5 h-3.5" />
              <span>Admin Panel</span>
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => navigate('/admin/debug')} className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50" style={{ color: '#F59E0B' }}>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Debug Panel</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};
